import { useMemo } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API } from 'api';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider/CacheableQueryClientProvider';

export type ReportWarningWordCategory =
  | 'catchall'
  | 'injury_fatality'
  | 'cause'
  | 'political'
  | 'evacuation';

export type ReportWarningWord = {
  word: string;
  category: ReportWarningWordCategory;
};

export type ReportWarningWordsByCategory = {
  [K in ReportWarningWordCategory]: string[];
};

export type UseReportWarningWordsResult = Omit<
  UseQueryResult<ReportWarningWord[]>,
  'data'
> & {
  warningWords: ReportWarningWord[];
  warningWordsByCategory: ReportWarningWordsByCategory;
};

const groupWarningWordsByCategory = (
  words: ReportWarningWord[],
): ReportWarningWordsByCategory => {
  const result: ReportWarningWordsByCategory = {
    catchall: [],
    injury_fatality: [],
    cause: [],
    political: [],
    evacuation: [],
  };
  words.forEach(({ word, category }) => {
    if (category in result) {
      result[category].push(word);
    }
  });
  return result;
};

const fetchReportWarningWords = async (): Promise<ReportWarningWord[]> => {
  const response = await API.get('report_warning_words');
  return response.data;
};

export const useReportWarningWords = (): UseReportWarningWordsResult => {
  const { data, ...result } = useQuery<ReportWarningWord[]>(
    {
      queryKey: ['reportWarningWords'],
      queryFn: fetchReportWarningWords,
      staleTime: 60 * 60 * 1000, // 1 hour
      initialData: [],
      initialDataUpdatedAt: 0,
    },
    persistedQueryClient,
  );

  const warningWords = data;

  const warningWordsByCategory = useMemo(
    () => groupWarningWordsByCategory(warningWords),
    [warningWords],
  );

  return { warningWords, warningWordsByCategory, ...result };
};
